import type { Integration } from './catalog.js';
import { BRAND_MARKS } from './logos.js';

// One integration's mark as inline SVG, for the site (rendered into the HTML at build time) and the portal (the same
// string). A brand's own mark from simple-icons, unmodified: its single path in its own colour on a light tile, so the
// colours read in both themes. Brands simple-icons does not carry (Salesforce, LinkedIn, Smartlead at their owners'
// request) get a neutral monogram rather than a redrawn logo; Outlevo's own features get a plain line glyph.

const GLYPHS: Record<string, string> = {
  webhook: '<path d="M9 7.5a3 3 0 1 1 4.5 2.6L16 15M7.5 16.5a3 3 0 1 1 2.8-4.1L13 8M16.5 18H11a3 3 0 1 1 0-1.5"/>',
  api: '<path d="M8 8l-4 4 4 4M16 8l4 4-4 4M13.5 5l-3 14"/>',
  file: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h4"/>',
  mailbox: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
  visitors: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>',
};

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The tile's inner SVG; `label` names it for assistive technology (the card's own text usually does, so it is optional). */
export function logoSvg(i: Pick<Integration, 'name' | 'icon'>, o: { size?: number; label?: boolean } = {}): string {
  const size = o.size ?? 28;
  const a11y = o.label ? `role="img" aria-label="${esc(`${i.name} logo`)}"` : 'aria-hidden="true" focusable="false"';
  if ('brand' in i.icon) {
    const m = BRAND_MARKS[i.icon.brand];
    if (m) return `<svg class="int-mark" viewBox="0 0 24 24" width="${size}" height="${size}" ${a11y}><path fill="#${m.hex}" d="${m.path}"/></svg>`;
  }
  if ('monogram' in i.icon) {
    return `<svg class="int-mark" viewBox="0 0 24 24" width="${size}" height="${size}" ${a11y}><rect width="24" height="24" rx="5" fill="#5c554d"/><text x="12" y="16" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="10" font-weight="700" fill="#ffffff">${esc(i.icon.monogram)}</text></svg>`;
  }
  const g = 'glyph' in i.icon ? GLYPHS[i.icon.glyph] ?? GLYPHS.api : GLYPHS.api;
  return `<svg class="int-mark" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="#5c554d" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" ${a11y}>${g}</svg>`;
}

/** Whether the tile shows someone else's trademark (the pages that do carry the trademark note). */
export const isBrandMark = (i: Pick<Integration, 'icon'>) => 'brand' in i.icon && Boolean(BRAND_MARKS[i.icon.brand]);
